import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Navigation, CheckCircle2, ChevronDown, Loader2, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { locationService, Region, City } from '../../services/location.service';
import toast from 'react-hot-toast';

export interface LocationValue {
    city: string;
    cityId?: string;
    address: string;
    latitude?: number;
    longitude?: number;
    region?: string;
    regionId?: string;
}

interface LocationInputProps {
    value: LocationValue;
    onChange: (value: LocationValue) => void;
    required?: boolean;
    className?: string;
    layout?: 'vertical' | 'horizontal';
    hideLabels?: boolean;
    hideAddress?: boolean;
    hideRegion?: boolean;
    disableGlobalSelection?: boolean;
    disableGps?: boolean;
    placeholder?: string;
    icon?: React.ReactNode;
}

// Nominatim address result
interface NominatimResult {
    place_id: number;
    display_name: string;
    lat: string;
    lon: string;
    address: {
        road?: string;
        house_number?: string;
        neighbourhood?: string;
        suburb?: string;
        city?: string;
        town?: string;
        village?: string;
    };
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
}

export const LocationInput: React.FC<LocationInputProps> = ({
    value,
    onChange,
    required = false,
    className = '',
    layout = 'vertical',
    hideLabels = false,
    disableGps = false,
    hideAddress = false,
    hideRegion = false,
    disableGlobalSelection = false,
    placeholder,
    icon
}) => {
    const { t, i18n } = useTranslation();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const addressDropdownRef = useRef<HTMLDivElement>(null);

    // Data
    const [regions, setRegions] = useState<Region[]>([]);
    const [allCities, setAllCities] = useState<City[]>([]);
    const [filteredCities, setFilteredCities] = useState<City[]>([]);
    const [selectedRegionId, setSelectedRegionId] = useState<string>(value.regionId || '');
    const [isLoading, setIsLoading] = useState(true);

    // UI State
    const [isCityOpen, setIsCityOpen] = useState(false);
    const [cityInputValue, setCityInputValue] = useState(value.city || '');

    // Address UI State
    const [addressInput, setAddressInput] = useState(value.address || '');
    const [addressSuggestions, setAddressSuggestions] = useState<NominatimResult[]>([]);
    const [isAddressOpen, setIsAddressOpen] = useState(false);
    const [isSearchingAddress, setIsSearchingAddress] = useState(false);
    const debouncedAddress = useDebounce(addressInput, 400);

    const isArabic = i18n.language === 'ar' || i18n.language === 'darija_ar';

    // Load Data
    useEffect(() => {
        const loadData = async () => {
            try {
                setIsLoading(true);
                const [regionsData, citiesData] = await Promise.all([
                    locationService.getRegions(),
                    locationService.getAllCities()
                ]);
                setRegions(regionsData ?? []);
                setAllCities(citiesData ?? []);
                setFilteredCities((citiesData ?? []).slice(0, 50));
            } catch (err) {
                console.error('Error loading locations:', err);
                toast.error(t('errors.loadingLocations'));
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
    }, []);

    // Helper to get display name
    const getCityDisplayName = (city: City) => isArabic ? city.nameAr : city.name;

    // Sync external value changes to input
    useEffect(() => {
        if (value.city !== cityInputValue) {
            // Only update if value matches a known city or is empty
            // We don't want to overwrite user typing unless it's a confirmed change
            // But here we must sync if parent updates.
            // Check if value.city corresponds to the current typing or if it's a new selection
            if (value.city) {
                setCityInputValue(value.city);
            } else if (!value.city && !isCityOpen) {
                setCityInputValue('');
            }
        }
    }, [value.city]);

    // Update filtered cities based on input
    // Filter cities based on region selection and search
    useEffect(() => {
        let cities = allCities;
        // Only filter by region if not hidden
        if (selectedRegionId && !hideRegion) {
            cities = cities.filter(c => c.regionId === selectedRegionId);
        }

        if (cityInputValue) {
            const search = cityInputValue.toLowerCase();
            // If input matches exactly the selected city, show all (or filtered by region)
            // But usually we want to filter by what they type.
            // If they just selected it, the input is full name. Filtering by full name shows that one city.
            cities = cities.filter(c =>
                c.name.toLowerCase().includes(search) ||
                c.nameAr.includes(cityInputValue)
            );
        }

        setFilteredCities(cities.slice(0, 50));
    }, [cityInputValue, allCities, selectedRegionId]);

    // Sync Address
    useEffect(() => {
        if (value.address !== addressInput) {
            setAddressInput(value.address || '');
        }
    }, [value.address]);

    // Click Outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsCityOpen(false);
                // logic moved to onBlur
            }
            if (addressDropdownRef.current && !addressDropdownRef.current.contains(event.target as Node)) {
                setIsAddressOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleCityInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCityInputValue(e.target.value);
        setIsCityOpen(true);
        // We generally don't clear the selected value immediately on type,
        // or we do? UX choice. Usually clearing value on type is safer for consistency.
        // But let's allow typing to filter without clearing until selection.
    };

    const handleRegionChange = (regionId: string) => {
        setSelectedRegionId(regionId);
        const region = regions.find(r => r.id === regionId);
        if (value.cityId) {
            const city = allCities.find(c => c.id === value.cityId);
            if (city && city.regionId !== regionId) {
                // Clear city if not in new region
                onChange({ ...value, city: '', cityId: undefined, region: region?.name, regionId: regionId });
                setCityInputValue('');
                return;
            }
        }
        onChange({ ...value, region: region?.name, regionId: regionId });
    };

    const handleCitySelect = (city: City) => {
        const region = regions.find(r => r.id === city.regionId);
        setSelectedRegionId(city.regionId);
        setCityInputValue(getCityDisplayName(city));
        onChange({
            ...value,
            city: getCityDisplayName(city),
            cityId: city.id,
            region: region?.name,
            regionId: city.regionId,
            latitude: city.lat,
            longitude: city.lon
        });
        setIsCityOpen(false);
    };

    const handleClearCity = (replacementText: string = '') => {
        onChange({ ...value, city: replacementText, cityId: undefined, region: '', regionId: '', latitude: undefined, longitude: undefined, address: '' });
        setCityInputValue(replacementText);
        setSelectedRegionId('');
        // Don't close dropdown, maybe user wants to re-select
    };

    const matchCityFromName = (name: string) => {
        if (!name) return null;
        const search = name.toLowerCase().trim();
        return allCities.find(c =>
            c.name.toLowerCase() === search ||
            c.nameAr === name.trim()
        );
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        if (event.relatedTarget instanceof Node && dropdownRef.current?.contains(event.relatedTarget)) {
            return;
        }

        // If we have input but no cityId (or city doesn't match input), try to match or set custom
        if (cityInputValue && (!value.cityId || value.city !== cityInputValue)) {
            const matchedCity = matchCityFromName(cityInputValue);
            if (matchedCity) {
                handleCitySelect(matchedCity);
            } else {
                handleCustomCity();
            }
        }
    };

    const handleCustomCity = () => {
        if (cityInputValue) {
            // If input equals current city, do nothing
            if (value.city === cityInputValue) return;

            onChange({ ...value, city: cityInputValue, cityId: undefined });
            setIsCityOpen(false);
        }
    };

    // Address Search Effect (Copied logic)
    useEffect(() => {
        if (!debouncedAddress || debouncedAddress.length < 3 || !value.city) {
            setAddressSuggestions([]);
            return;
        }
        const searchAddress = async () => {
            setIsSearchingAddress(true);
            try {
                const cityContext = value.city || 'Maroc';
                const query = `${debouncedAddress}, ${cityContext}, Morocco`;
                const response = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=5&countrycodes=ma`, {
                    headers: { 'Accept-Language': i18n.language === 'ar' ? 'ar' : 'fr', 'User-Agent': 'Bricolat/1.0' }
                });
                const data = await response.json();
                setAddressSuggestions(data);
            } catch (error) { console.error(error); } finally { setIsSearchingAddress(false); }
        };
        searchAddress();
    }, [debouncedAddress, value.city, i18n.language]);

    const handleAddressSelect = (result: NominatimResult) => {
        const { road, house_number, neighbourhood, suburb } = result.address;
        let cleanAddress = '';
        if (house_number) cleanAddress += house_number + ' ';
        if (road) cleanAddress += road;
        else if (neighbourhood) cleanAddress += neighbourhood;
        else if (suburb) cleanAddress += suburb;
        if (!cleanAddress) cleanAddress = result.display_name.split(',')[0];

        setAddressInput(cleanAddress);
        onChange({
            ...value,
            address: cleanAddress,
            latitude: parseFloat(result.lat),
            longitude: parseFloat(result.lon)
        });
        setIsAddressOpen(false);
    };

    const handleGeolocation = () => {
        if ('geolocation' in navigator) {
            const toastId = toast.loading(t('locationInput.locating'));
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude } = position.coords;
                    onChange({ ...value, latitude, longitude }); // Keep existing city/address? Or clear?
                    // Typically GPS means "Current Location" regardless of city text. 
                    // But for search we might want to fill "Ma position".
                    setCityInputValue(t('locationInput.currentLocation'));
                    toast.success(t('locationInput.gpsSuccess'), { id: toastId });
                },
                (error) => { toast.error(t('locationInput.gpsError'), { id: toastId }); }
            );
        }
    };


    return (
        <div className={className}>
            <div className={`grid gap-3 ${layout === 'horizontal' ? 'grid-cols-1 md:grid-cols-3 items-end' : 'grid-cols-1 gap-y-4'}`}>

                {/* Region */}
                {!hideRegion && (
                    <div>
                        {!hideLabels && <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('locationInput.region')}</label>}
                        <div className="relative">
                            <select
                                value={selectedRegionId}
                                onChange={(e) => handleRegionChange(e.target.value)}
                                disabled={isLoading}
                                className={`w-full pl-4 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none cursor-pointer transition-all outline-none`}
                            >
                                <option value="">{t('locationInput.allRegions')}</option>
                                {regions.map(r => <option key={r.id} value={r.id}>{isArabic ? r.nameAr : r.name}</option>)}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                        </div>
                    </div>
                )}

                {/* City - Direct Input */}
                <div className="relative" ref={dropdownRef}>
                    {!hideLabels && <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('locationInput.city')} {required && <span className="text-red-500">*</span>}</label>}
                    <div className="relative">
                        {icon && (
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none z-10">
                                {icon}
                            </div>
                        )}
                        <input
                            type="text"
                            value={cityInputValue}
                            onChange={handleCityInputChange}
                            onFocus={() => setIsCityOpen(true)}
                            onBlur={handleBlur}
                            placeholder={placeholder || t('locationInput.selectCity')}
                            className={`w-full ${icon ? 'pl-12' : 'pl-4'} pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white focus:border-blue-500 transition-all outline-none font-medium truncate placeholder:text-gray-400 text-gray-900`}
                        />

                        {/* Clear Button */}
                        {cityInputValue && (
                            <button
                                onClick={() => handleClearCity('')}
                                aria-label={t('locationInput.clear')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-200"
                            >
                                <X size={16} />
                            </button>
                        )}

                        {/* Dropdown */}
                        {isCityOpen && (
                            <div className="absolute z-50 mt-2 w-full min-w-[16rem] bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden animate-fade-in text-start">
                                <div className="max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200">
                                    {/* Default Options (Reset/GPS) */}
                                    <div className="border-b border-gray-50">
                                        {!disableGlobalSelection && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    handleClearCity(t('locationInput.allMorocco'));
                                                    setIsCityOpen(false);
                                                }}
                                                className="w-full px-4 py-3 cursor-pointer hover:bg-green-50 flex items-center gap-3 text-left"
                                            >
                                                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                                                    <MapPin size={16} className="text-green-600" />
                                                </div>
                                                <span className="font-medium text-green-700">{t('locationInput.allMorocco')}</span>
                                            </button>
                                        )}
                                        {!disableGps && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    handleGeolocation();
                                                    setIsCityOpen(false);
                                                }}
                                                className="w-full px-4 py-3 cursor-pointer hover:bg-blue-50 flex items-center gap-3 text-left"
                                            >
                                                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                                                    <Navigation size={16} className="text-blue-600" />
                                                </div>
                                                <span className="font-medium text-blue-700">{t('locationInput.useMyLocation')}</span>
                                            </button>
                                        )}
                                    </div>

                                    {/* City List */}
                                    {filteredCities.length > 0 ? (
                                        filteredCities.map(city => (
                                            <button
                                                type="button"
                                                key={city.id}
                                                onClick={() => handleCitySelect(city)}
                                                className={`w-full px-4 py-2.5 cursor-pointer hover:bg-blue-50 flex items-center justify-between text-left ${value.cityId === city.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
                                            >
                                                <div>
                                                    <div className="font-medium">{getCityDisplayName(city)}</div>
                                                    {!selectedRegionId && <div className="text-xs text-gray-400">{regions.find(r => r.id === city.regionId)?.name}</div>}
                                                </div>
                                                {value.cityId === city.id && <CheckCircle2 size={16} className="text-blue-600" />}
                                            </button>
                                        ))
                                    ) : (
                                        <div className="p-4 text-center text-gray-500 text-sm">
                                            {cityInputValue ? t('locationInput.noCityFound') : t('common.typeToSearch')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Address Input (Optional) */}
                {!hideAddress && (
                    <div ref={addressDropdownRef} className="relative">
                        {!hideLabels && <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('locationInput.address')} <span className="font-normal text-gray-400">({t('common.optional')})</span></label>}
                        <div className="relative flex shadow-sm rounded-xl h-[46px]">
                            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10">
                                {isSearchingAddress ? <Loader2 size={18} className="animate-spin" /> : <MapPin size={18} />}
                            </div>
                            <input
                                type="text"
                                value={addressInput}
                                onChange={(e) => {
                                    setAddressInput(e.target.value);
                                    setIsAddressOpen(true);
                                    onChange({ ...value, address: e.target.value });
                                }}
                                disabled={!value.city}
                                placeholder={value.city ? t('locationInput.addressPlaceholder') : t('locationInput.selectCityFirst')}
                                className={`w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none bg-gray-50 focus:bg-white text-gray-900 ${!value.city ? 'opacity-50 cursor-not-allowed' : ''}`}
                            />
                        </div>
                        {/* Address Dropdown */}
                        {isAddressOpen && addressSuggestions.length > 0 && (
                            <div className="absolute z-50 mt-1 w-full bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden animate-fade-in text-left">
                                <div className="max-h-48 overflow-y-auto">
                                    {addressSuggestions.map(result => (
                                        <div key={result.place_id} onClick={() => handleAddressSelect(result)} className="px-4 py-2.5 cursor-pointer hover:bg-blue-50 border-b border-gray-50 last:border-0">
                                            <div className="font-medium text-gray-900 text-sm">{result.address.road || result.display_name.split(',')[0]}</div>
                                            <div className="text-xs text-gray-500 truncate">{result.display_name}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
